<template>
  <v-list-item v-if="scope" class="pa-0">
    <template #default>
      <div class="d-flex mt-0">
        <v-col class="py-1" cols="6">
          <span class="permission-section__title subtitle2">
            {{ title }}
          </span>
        </v-col>
        <v-col class="py-1" cols="6">
          <span class="permission-section__title subtitle2">
            {{ $t(`accessControlPage.roles.details.permissions`) }}
          </span>
        </v-col>
      </div>
      <div
        v-for="(permissionValue, permissionName) in scope"
        :key="permissionName"
        class="d-flex"
        :class="{ 'mb-1': showPermission(permissionName) }"
      >
        <template v-if="showModulesDisabledBySubscription || showPermission(permissionName)">
          <v-col class="py-0" cols="6">
            <span class="body2 mb-1 text-wrap">
              {{
                isNestedPermission(permissionValue) || isSpecialPermission(permissionValue)
                  ? $t(`accessControlPage.roles.scopes.${scopeName}.${permissionName}.title`)
                  : $t(`accessControlPage.roles.scopes.${scopeName}.${permissionName}`)
              }}
            </span>
          </v-col>
          <v-col class="py-0" cols="6">
            <template v-if="isNestedPermission(permissionValue)">
              <span class="body2 mb-1">
                {{
                  $t(
                    `accessControlPage.roles.details.permissionModes.${
                      (permissionValue as NestedPermissions).accessMode
                    }`
                  )
                }}
              </span>
              <ul
                class="d-flex flex-column ml-4"
                v-if="
                  (permissionValue as NestedPermissions).accessMode ===
                    RolePermissionAccessMode.EDIT &&
                  (permissionValue as NestedPermissions).editAccessModePermission
                "
              >
                <template
                  v-for="(nestedPermissionValue, nestedPermissionName) in (
                    permissionValue as NestedPermissions
                  ).editAccessModePermission"
                >
                  <li
                    v-if="nestedPermissionValue"
                    :key="nestedPermissionName"
                    class="body2 mb-1 text-wrap"
                  >
                    {{
                      $t(
                        `accessControlPage.roles.scopes.${scopeName}.${permissionName}.editAccessModePermission.${nestedPermissionName}`
                      )
                    }}
                  </li>
                </template>
              </ul>
            </template>
            <template v-else-if="isSpecialPermission(permissionValue)">
              <ul
                v-if="hasEnabledSpecificPermissions(permissionValue)"
                class="d-flex flex-column ml-4"
              >
                <template v-for="(nestedPermissionValue, nestedPermissionName) in permissionValue">
                  <li
                    v-if="nestedPermissionValue"
                    class="body2 mb-1 text-wrap"
                    :key="nestedPermissionName"
                  >
                    {{
                      $t(
                        `accessControlPage.roles.scopes.${scopeName}.${permissionName}.editAccessModePermission.${nestedPermissionName}`
                      )
                    }}
                  </li>
                </template>
              </ul>
              <div class="body2 mb-1 text-wrap" v-else>
                {{ $t("general.none") }}
              </div>
            </template>
            <span v-else class="body2 mb-1">
              {{ $t(`accessControlPage.roles.details.permissionModes.${permissionValue}`) }}
            </span>
          </v-col>
        </template>
      </div>
    </template>
  </v-list-item>
</template>

<script setup lang="ts" generic="T">
import type {
  NestedPermissions,
  RolePermissions,
  SpecialRolesPermissions,
} from "@/_store/roles.module";
import { useRolesPermissions } from "@/composables/useRolesPermissions";
import {
  MspRolePermissionScopes,
  RolePermissionAccessMode,
  RolePermissionsScope,
} from "@/constants/roles";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const i18n = useI18n();

const props = defineProps<{
  scope: RolePermissions[RolePermissionsScope | MspRolePermissionScopes];
  scopeName: RolePermissionsScope | MspRolePermissionScopes;
  showModulesDisabledBySubscription?: boolean;
  scopeTitle?: string;
}>();

const title = computed(() => {
  return props.scopeTitle || i18n.t(`accessControlPage.roles.scopes.${props.scopeName}.title`);
});

const { isNestedPermission, showPermission, isSpecialPermission } = useRolesPermissions(
  props.scopeName
);

const hasEnabledSpecificPermissions = (permission: SpecialRolesPermissions): boolean => {
  return Object.values(permission).some((v: boolean | null) => !!v);
};

defineExpose({
  isNestedPermission,
  showPermission,
  isSpecialPermission,
  hasEnabledSpecificPermissions,
});
</script>

<style scoped></style>
